import {
  captureAttribution,
  getCountry,
  hasCookieConsentForCategory,
  isConsentRequiredCountry,
  needsCookieConsent,
  saveCookieConsent,
  CookieConsentCategory,
} from "@flowstepdesign/shared";
import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import {
  ClientOnly,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { useEffect, useState } from "react";

import { CookieConsentBar } from "@/components/CookieConsentBar";
import { config } from "@/config";
import { useTrackPageChanges } from "@/lib/useTrackPageChanges";

export function RootDocument({ children }: { children: React.ReactNode }) {
  useTrackPageChanges();

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const normalizedPathname =
    pathname === "/" ? pathname : pathname.replace(/\/+$/, "");
  const canonicalUrl = new URL(
    normalizedPathname,
    `https://${config.host}`,
  ).toString();

  // Geo-resolution gate: we delay rendering the consent banner and loading GA
  // until we know whether this visitor's country legally requires a banner.
  // Visitors outside EEA+UK+CH get analytics+marketing consent auto-granted so
  // GA and Mixpanel fire without showing the banner.
  const [geoResolved, setGeoResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If the user has already made a choice, never override it.
      if (!needsCookieConsent()) {
        if (!cancelled) setGeoResolved(true);
        return;
      }
      const country = await getCountry(config.ipinfo.token);
      if (cancelled) return;
      if (country && !isConsentRequiredCountry(country)) {
        saveCookieConsent(
          { necessary: true, analytics: true, marketing: true },
          config.domain,
        );
      }
      setGeoResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Captures first-touch UTM attribution + geolocation into a cookie shared
  // with app.flowstep.ai, so it survives the redirect to signup. This is the
  // actual public landing surface for most traffic (search, ads, socials) —
  // app.flowstep.ai only sees a cross-domain hop from here in most cases.
  //
  // Gated on geoResolved so this runs after the effect above rather than
  // alongside it: getCountry() has no in-flight-request dedup, so firing
  // both concurrently on a cold cache (exactly the first-touch case this
  // targets) doubles up requests to ipinfo. By the time geoResolved flips,
  // that effect has already populated getCountry()'s 24h cache, so this call
  // reuses it instead of hitting the network again.
  useEffect(() => {
    if (!geoResolved) return;
    void captureAttribution({
      ipinfoToken: config.ipinfo.token,
      domain: config.domain,
    });
  }, [geoResolved]);

  // Load Google Analytics script dynamically — gated on geo resolution so we
  // pick up the auto-granted consent above before evaluating.
  useEffect(() => {
    if (!geoResolved) return;
    if (
      config.google?.tag_id &&
      hasCookieConsentForCategory(CookieConsentCategory.enum.analytics) &&
      typeof window !== "undefined"
    ) {
      // Create inline script for gtag initialization
      const inlineScript = document.createElement("script");
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${config.google.tag_id}');
      `;
      document.head.appendChild(inlineScript);

      // Create and append the external GA script
      const externalScript = document.createElement("script");
      externalScript.src = `https://www.googletagmanager.com/gtag/js?id=${config.google.tag_id}`;
      externalScript.async = true;
      document.head.appendChild(externalScript);
    }
  }, [geoResolved]);

  return (
    <html lang="en">
      <head>
        <HeadContent />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Must render before <Scripts/> (end of <body>) — document order
            guarantees this executes first, so config.ts's client branch
            (window.__FLOWSTEP_CONFIG__) is already populated by the time
            the hydration bundle evaluates, including module-scope reads
            like lib/sentry.ts's initSentry() call in __root.tsx. `<` is
            escaped so a value can never break out of the script tag. */}
        <script
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `window.__FLOWSTEP_CONFIG__=${JSON.stringify(config).replace(/</g, "\\u003c")}`,
          }}
        />
      </head>
      <body>
        {children}
        <ClientOnly>{geoResolved && <CookieConsentBar />}</ClientOnly>
        {!config.isDeployed && (
          <TanStackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}

export function GlobalErrorBoundary({ error }: { error: Error }) {
  // Report error to Sentry
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h1>Oops! Something went wrong</h1>
      <pre
        style={{
          textAlign: "left",
          padding: "1rem",
          background: "#f5f5f5",
          borderRadius: "4px",
        }}
      >
        {error.message}
      </pre>
    </div>
  );
}
